import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const SOURCE_LABELS = {
  fresh_unit: "Take out from fresh unit",
  damaged_set: "Take out from damaged set",
  refurbished_set: "Take out from refurbished set",
};

const blank = {
  complaint_id: "",
  source_type: "fresh_unit",
  source_brand: "",
  source_product: "",
  source_model: "",
  source_serial: "",
  part_name: "",
  part_code: "",
  quantity: 1,
  reason: "",
  remarks: "",
};

export default function SparesPartTakeout() {
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);
  // Complaints that already have PNA source approved — these are the only ones eligible for takeout.
  const [eligible, setEligible] = useState([]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reloadEligible = async () => {
    // Spares can view complaints — filter client-side to those with pna_source approved.
    try {
      const { data } = await api.get("/complaints");
      setEligible((data || []).filter((c) => c.pna_source && c.pna_source_approved_at));
    } catch {
      setEligible([]);
    }
  };

  const reloadRecent = async () => {
    try {
      const { data } = await api.get("/spares/part-takeouts", { params: { limit: 25 } });
      setRecent(data || []);
    } catch {
      setRecent([]);
    }
  };

  useEffect(() => {
    reloadEligible();
    reloadRecent();
  }, []);

  const onPickComplaint = (cid) => {
    const c = eligible.find((x) => x.id === cid);
    if (!c) {
      update("complaint_id", cid);
      return;
    }
    setForm((f) => ({
      ...f,
      complaint_id: cid,
      source_type: c.pna_source || f.source_type,
      source_brand: c.brand || f.source_brand,
      source_product: c.product_name || f.source_product,
      source_model: c.product_model_number || f.source_model,
      part_name: c.pna_part_details || c.parts_required || f.part_name,
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.complaint_id) {
      toast.error("Select a complaint (with PNA source approved)");
      return;
    }
    if (!form.part_name.trim()) {
      toast.error("Part name is required");
      return;
    }
    if (!form.quantity || Number(form.quantity) <= 0) {
      toast.error("Quantity must be greater than zero");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/spares/part-takeout", { ...form, quantity: Number(form.quantity) });
      toast.success("Part takeout recorded");
      setForm({ ...blank });
      reloadRecent();
    } catch (err) {
      toast.error("Failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="part-takeout-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Part Takeout</h1>
        <p className="text-sm text-slate-500 mt-1">
          Record parts taken from a <b>fresh / damaged / refurbished</b> unit. Requires Service Head approval on the linked complaint.
        </p>
      </div>

      <Card>
        <CardContent className="p-5">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Fld label="Linked Complaint (PNA-approved)" required className="md:col-span-2">
              <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm" value={form.complaint_id} onChange={(e) => onPickComplaint(e.target.value)} data-testid="complaint-select" required>
                <option value="">Select complaint…</option>
                {eligible.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.complaint_number} · {c.customer_name} · {c.brand} · source approved: {c.pna_source}
                  </option>
                ))}
              </select>
              {eligible.length === 0 && (
                <div className="text-xs text-amber-700 mt-1" data-testid="no-eligible-warning">
                  No complaints have a PNA source approved by Service Head yet. Ask Service Head to approve the PNA source first.
                </div>
              )}
            </Fld>

            <Fld label="Source Type" required>
              <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm" value={form.source_type} onChange={(e) => update("source_type", e.target.value)} data-testid="source-type-select">
                {Object.entries(SOURCE_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </Fld>
            <Fld label="Source Unit Brand">
              <Input value={form.source_brand} onChange={(e) => update("source_brand", e.target.value)} data-testid="source-brand-input" />
            </Fld>
            <Fld label="Source Unit Product/Model">
              <Input value={form.source_product} onChange={(e) => update("source_product", e.target.value)} data-testid="source-product-input" />
            </Fld>
            <Fld label="Source Unit Model #">
              <Input value={form.source_model} onChange={(e) => update("source_model", e.target.value)} data-testid="source-model-input" />
            </Fld>
            <Fld label="Source Serial #">
              <Input value={form.source_serial} onChange={(e) => update("source_serial", e.target.value)} placeholder="if available" data-testid="source-serial-input" />
            </Fld>
            <Fld label="Part Name" required>
              <Input value={form.part_name} onChange={(e) => update("part_name", e.target.value)} required data-testid="part-name-input" />
            </Fld>
            <Fld label="Part Code">
              <Input value={form.part_code} onChange={(e) => update("part_code", e.target.value)} data-testid="part-code-input" />
            </Fld>
            <Fld label="Quantity" required>
              <Input type="number" min={1} value={form.quantity} onChange={(e) => update("quantity", parseInt(e.target.value) || 0)} data-testid="qty-input" required />
            </Fld>
            <Fld label="Reason">
              <Input value={form.reason} onChange={(e) => update("reason", e.target.value)} placeholder="e.g. PNA, urgent" data-testid="reason-input" />
            </Fld>
            <Fld label="Remarks" className="md:col-span-2">
              <Input value={form.remarks} onChange={(e) => update("remarks", e.target.value)} data-testid="remarks-input" />
            </Fld>

            <div className="md:col-span-2 flex justify-end gap-2">
              <Button type="submit" disabled={submitting} className="bg-slate-900 text-white" data-testid="submit-takeout">
                {submitting ? "Saving..." : "Record Takeout"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card data-testid="takeout-history">
        <CardContent className="p-4">
          <div className="font-heading font-semibold text-slate-900 mb-3">Recent Takeouts</div>
          {recent.length === 0 && <div className="text-sm text-slate-500">No takeouts recorded yet.</div>}
          {recent.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <Th>When</Th><Th>Complaint</Th><Th>Source</Th><Th>Part</Th>
                    <Th align="right">Qty</Th><Th>By</Th><Th>Approved By</Th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((t) => (
                    <tr key={t.id} data-testid={`takeout-row-${t.id}`}>
                      <td className="text-slate-700 text-xs">{(t.ts || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="font-mono">{t.complaint_number}</td>
                      <td className="text-slate-700">{(SOURCE_LABELS[t.source_type] || t.source_type).replace(/^Take out from /, "")}</td>
                      <td>{t.part_name}{t.part_code && <span className="font-mono text-xs text-slate-500 ml-1">({t.part_code})</span>}</td>
                      <td className="text-right font-mono">{t.quantity}</td>
                      <td className="text-slate-700">{t.taken_out_by}</td>
                      <td className="text-slate-700 text-xs">{t.approved_by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Fld({ label, children, required, className }) {
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold`}>{children}</th>;
}
